import React from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TrendingUp } from 'lucide-react'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface FraudTrendsChartProps {
  timeRange: TimeRange
  compareEnabled: boolean
}

// Mock data generator based on real patterns
const generateTrendsData = () => {
  const data = []
  const baseDate = new Date()
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(baseDate)
    date.setDate(date.getDate() - i)
    
    // Simulate realistic patterns
    const isWeekend = date.getDay() === 0 || date.getDay() === 6
    const isBusinessHours = date.getHours() >= 9 && date.getHours() <= 17
    
    // Lower volume on weekends, higher fraud rate during business hours
    const totalLeads = isWeekend ? 
      Math.floor(Math.random() * 150) + 50 : 
      Math.floor(Math.random() * 200) + 200
    
    // Fraud rate varies between 25-40%, higher during business hours
    const fraudRate = isBusinessHours ? 
      0.28 + Math.random() * 0.12 : 
      0.22 + Math.random() * 0.08
    
    const fraudLeads = Math.floor(totalLeads * fraudRate)
    const legitimateLeads = totalLeads - fraudLeads
    
    data.push({
      date: date.toISOString().split('T')[0],
      dateLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      totalLeads,
      fraudLeads,
      legitimateLeads,
      fraudRate: Math.round(fraudRate * 100),
      moneySaved: fraudLeads * 30, // $30 per fraudulent lead blocked
      detectionAccuracy: Math.round((95 + Math.random() * 5) * 10) / 10
    })
  }
  
  return data
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-4 min-w-[250px]">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Total Leads:</span>
            <span className="font-medium">{data.totalLeads.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-red-600">Fraudulent:</span>
            <span className="font-medium text-red-600">{data.fraudLeads} ({data.fraudRate}%)</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-green-600">Legitimate:</span>
            <span className="font-medium text-green-600">{data.legitimateLeads}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Money Saved:</span>
            <span className="font-medium">${data.moneySaved.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Accuracy:</span>
            <span className="font-medium">{data.detectionAccuracy}%</span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export const FraudTrendsChart: React.FC<FraudTrendsChartProps> = ({
  timeRange,
  compareEnabled
}) => {
  const data = generateTrendsData()
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Fraud Trends Over Time
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Last 30 Days
            </Badge>
            {compareEnabled && (
              <Badge variant="secondary" className="text-xs">
                vs Previous Period
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="fraudGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="legitimateGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="dateLabel" 
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="rect"
              />
              
              <Area
                type="monotone"
                dataKey="totalLeads"
                stackId="1"
                stroke="#3B82F6"
                fill="url(#totalGradient)"
                strokeWidth={2}
                name="Total Leads"
              />
              <Area
                type="monotone"
                dataKey="fraudLeads"
                stackId="2"
                stroke="#EF4444"
                fill="url(#fraudGradient)"
                strokeWidth={2}
                name="Fraudulent Leads"
              />
              <Area
                type="monotone"
                dataKey="legitimateLeads"
                stackId="3"
                stroke="#10B981"
                fill="url(#legitimateGradient)"
                strokeWidth={2}
                name="Legitimate Leads"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Key Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">32%</p>
            <p className="text-sm text-muted-foreground">Average Fraud Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">$23,400</p>
            <p className="text-sm text-muted-foreground">Total Money Saved</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">95.3%</p>
            <p className="text-sm text-muted-foreground">Detection Accuracy</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}